import type { FC, ReactNode } from 'react'
import React from 'react'
import type { ButtonProps } from '@material-ui/core'
import { Button as MuiButton } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import type { ITheme } from '@homeviews/styles'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      textTransform: 'initial',
      paddingTop: '11px',
      paddingBottom: '12px',
      borderRadius: '5px',
      whiteSpace: 'nowrap',
      position: 'relative',
      '&.MuiButton-root.Mui-disabled': {
        color: 'inherit',
        opacity: 0.75,
        '&.MuiButton-textPrimary': {
          color: theme.colors.grey600,
        },
      },
      '&.MuiButton-sizeSmall': {
        paddingTop: 6.5,
        paddingBottom: 6.5,
        fontSize: `12px!important`,
      },
      fontWeight: 500,
    },
    dark: {
      color: theme.colors.grey600,
      border: `1px solid ${theme.colors.grey600}`,
    },
    outlined: {
      paddingTop: '10px',
      paddingBottom: '11px',
    },
    primary: {
      color: '#000433',
      backgroundColor: theme.colors.primary,
      '&:hover, &:focus': {
        backgroundColor: '#00f5c9',
      },
    },
    outlinedPrimary: {
      color: '#000433',
      border: `2px solid #00DEB6`,
      '&:hover, &:focus': {
        backgroundColor: '#00DEB6',
      },
    },
    containedPrimary: {
      border: `1px solid ${theme.colors.grey600}`,
      backgroundColor: theme.colors.grey600,
      color: `#000433 !important`,
      '&:hover, &:focus': {
        backgroundColor: theme.colors.grey600,
      },
    },
    containedGold: {
      color: `#000433 !important`,
      border: `1px solid #F5A512`,
      backgroundColor: '#F5A512',
      '&:hover, &:focus': {
        backgroundColor: '#F5A512',
        filter: 'brightness(1.15)',
      },
    },
    containedWhite: {
      border: `1px solid ${theme.colors.white}`,
      backgroundColor: theme.colors.white,
      color: `${theme.colors.grey800}!important`,
      '&:hover, &:focus': {
        backgroundColor: theme.colors.white,
      },
    },
    red: {
      color: theme.colors.red,
      border: `1px solid ${theme.colors.red}`,
    },
    isLoading: {
      pointerEvents: 'none',
      '& .MuiButton-label > *': {
        opacity: 0,
      },
      '& .MuiCircularProgress-root': {
        color: 'inherit',
        position: 'absolute',
        opacity: 1,
      },
    },
  }),
)
interface IProps extends ButtonProps {
  children?: ReactNode
  customColor?: string
  className?: string
  customVariant?:
    | 'outlinedPrimary'
    | 'containedPrimary'
    | 'containedGold'
    | 'containedWhite'
  isLoading?: boolean
  component?: 'label'
  htmlFor?: string
}

const Button: FC<IProps> = ({
  children,
  variant,
  className,
  customColor,
  customVariant,
  isLoading,
  ...rest
}) => {
  const classes = useStyles()

  return (
    <MuiButton
      className={clsx(
        classes.root,
        customColor === 'dark' && classes.dark,
        customColor === 'primary' && classes.primary,
        customColor === 'red' && classes.red,
        customColor === 'containedGold' && classes.containedGold,
        variant === 'outlined' && classes.outlined,
        isLoading && classes.isLoading,
        classes[customVariant] && classes[customVariant],
        className && className,
      )}
      {...rest}
    >
      <span>{children}</span>
      {isLoading && <CircularProgress size={20} />}
    </MuiButton>
  )
}

export default Button
